// API Types - matches server-side types

/**
 * API response interface for health check endpoint.
 */
export interface HealthResponse {
  uptime: number;
  service: string;
  version: string;
  timestamp: string;
  checks?: Record<string, HealthCheckResult>;
  status: 'healthy' | 'unhealthy' | 'degraded';
}

/**
 * Result of a single health check performed on a service component.
 */
export interface HealthCheckResult {
  message?: string;
  latencyMs?: number;
  status: 'pass' | 'fail' | 'warn';
}

// Mapping types

/** Constant object mapping rule type identifiers to their string values. */
export const RuleType = {
  METADATA: 'metadata',
  SYNTHETIC: 'synthetic',
  ONE_TO_ONE: 'one-to-one',
  ENRICHMENT: 'enrichment',
  MANY_TO_ONE: 'many-to-one',
  ONE_TO_MANY: 'one-to-many',
  CONDITIONAL: 'conditional',
  NORMALIZATION: 'normalization',
} as const;

/** Union type of all valid transformation rule type identifiers. */
export type RuleType = (typeof RuleType)[keyof typeof RuleType];

/**
 * All supported delete strategy values. Mirrors `DELETE_STRATEGIES` in `@nexus/core`, which the browser
 * bundle cannot import (see `lib/type-guards.ts`); keep the two in step.
 */
export const DELETE_STRATEGIES = ['hard', 'soft', 'skip'] as const;

/** Strategy for handling delete events of a mapping's source rows. */
export type DeleteStrategy = (typeof DELETE_STRATEGIES)[number];

/**
 * Checks if a value is a supported delete strategy.
 *
 * @param { unknown } value The value to check.
 * @returns { boolean } True if the value is one of `DELETE_STRATEGIES`.
 */
export const isDeleteStrategy = (value: unknown): value is DeleteStrategy =>
  DELETE_STRATEGIES.some((strategy) => strategy === value);

/**
 * Configuration for a single field transformation rule.
 */
export interface FieldMapping {
  ruleType: string;
  sourceField: string;
  targetField: string;
  /** Source table name */
  sourceTable: string;
  /** PostgreSQL data type of the source column (e.g. 'integer', 'real', 'citext') */
  sourceType?: string;
  config?: Record<string, unknown>;
}

/**
 * Entry for a source table in a multi-table mapping.
 */
export interface SourceTableEntry {
  /** Source table name */
  table: string;
  /** Schema name (default: 'public') */
  schema: string;
  /** Whether this is the primary (first-added) source table */
  isPrimary: boolean;
}

/** SQL join type for multi-table mappings */
export type JoinType = 'INNER' | 'LEFT';

/**
 * A single column pair in a join relationship.
 */
export interface JoinColumnPair {
  /** Key column on the left (parent) table */
  leftColumn: string;
  /** Foreign key column on the right (child) table */
  rightColumn: string;
}

/**
 * Definition of a join between two source tables in a multi-table mapping.
 */
export interface JoinDefinition {
  /** Left side table name */
  leftTable: string;
  /** Right side table name */
  rightTable: string;
  /** Join type */
  joinType: JoinType;
  /** Column pairs that define the join condition */
  columns: JoinColumnPair[];
}

/**
 * API response for a mapping configuration between source and target tables.
 */
export interface MappingResponse {
  /** The mapping's identity: one table set can have several mappings, one per adapter configuration */
  id: number;
  version: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  updatedBy?: string;
  /** Key derived from the source tables; a label, shared by every mapping of the same table set */
  mappingKey: string;
  targetTable: string;
  description?: string;
  /** Strategy for handling delete events ('hard' | 'soft' | 'skip') */
  deleteStrategy: string;
  updatedByName?: string;
  mappings: FieldMapping[];
  /** Shadow table name (e.g., '_shadow_orders') */
  shadowTableName?: string;
  /** Source tables used in this mapping */
  sourceTables: SourceTableEntry[];
  /** Join definitions between source tables */
  joinDefinitions?: JoinDefinition[];
  /** The adapter configuration the mapping sends its data through; null when none is chosen */
  nexusAdapterConfigId: number | null;
}

/**
 * Request payload for creating a new mapping.
 */
export interface CreateMappingRequest {
  isActive?: boolean;
  targetTable: string;
  description?: string;
  mappings: FieldMapping[];
  /**
   * Code of the adapter configuration, as an import file carries it. The code travels between
   * environments, not the ID. Required: an entry updates the saved mapping with the same mapping key and
   * the same configuration code, and otherwise adds a new mapping.
   */
  adapterConfigCode?: string;
  /** Primary key column names for the source table */
  primaryKeyColumns: string[];
  /**
   * Strategy for handling delete events. Absent keeps an existing mapping's saved value; a new mapping
   * gets `soft` on the server.
   */
  deleteStrategy?: DeleteStrategy;
  /** Source tables used in this mapping */
  sourceTables: SourceTableEntry[];
  /** Join definitions for multi-table mappings */
  joinDefinitions?: JoinDefinition[];
}

/**
 * Request payload for updating an existing mapping.
 */
export interface UpdateMappingRequest {
  isActive?: boolean;
  targetTable?: string;
  description?: string;
  expectedVersion?: number;
  mappings?: FieldMapping[];
  /** Source tables for multi-table mappings */
  sourceTables?: SourceTableEntry[];
  /** Join definitions for multi-table mappings */
  joinDefinitions?: JoinDefinition[];
}

/**
 * Paginated list of mappings returned from the API.
 */
export interface MappingListResponse {
  total: number;
  mappings: MappingResponse[];
}

// DLQ types
/**
 * Message stored in the Dead Letter Queue for failed processing.
 */
export interface DlqMessage {
  messageId: string;
  attemptCount: number;
  dlqTimestamp: string;
  originalSource: string;
  originalMessage?: unknown;
  originalMessageId?: string;
  originalOrderingKey?: string;
  originalPublishTime?: string;
  error: {
    name: string;
    code?: string;
    stack?: string;
    message: string;
    context?: Record<string, unknown>;
  };
}

/**
 * Paginated list of messages in a Dead Letter Queue.
 */
export interface DlqListResponse {
  total: number;
  source: string;
  messages: DlqMessage[];
}

/**
 * Statistics about messages in a Dead Letter Queue.
 */
export interface DlqStats {
  messageCount: number;
  oldestMessageAge?: number;
}

/**
 * Request payload for retrying failed messages in the DLQ.
 */
export interface RetryDlqRequest {
  messageIds: string[];
}

/**
 * Response indicating results of retrying DLQ messages.
 */
export interface RetryDlqResponse {
  failed: number;
  succeeded: number;
  errors: Array<{
    error: string;
    messageId: string;
  }>;
}

/**
 * Request payload for purging messages from the DLQ.
 */
export interface PurgeDlqRequest {
  messageIds?: string[];
  olderThanHours?: number;
}

/**
 * Response indicating number of messages purged from the DLQ.
 */
export interface PurgeDlqResponse {
  purged: number;
}

// Monitoring types
/**
 * Progress information for a table being processed in a bulk job.
 */
export interface TableProgress {
  tableName: string;
  totalRows: number;
  startedAt?: string;
  lastCursor?: string;
  totalChunks: number;
  completedAt?: string;
  processedRows: number;
  errorMessage?: string;
  chunksProcessed: number;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
}

/**
 * Progress and status information for an initial load or bulk job.
 */
export interface JobProgress {
  jobId: string;
  totalRows: number;
  startedAt: string;
  startedBy?: string;
  completedAt?: string;
  environment?: string;
  retailerCode?: string;
  processedRows: number;
  errorMessage?: string;
  tables: TableProgress[];
  retailerEnvironmentId?: string;
  status: 'pending' | 'running' | 'paused' | 'completed' | 'failed' | 'cancelled';
}

/**
 * Real-time synchronization status for a source-target table pair.
 */
export interface TableSyncStatus {
  isActive: boolean;
  sourceTable: string;
  targetTable: string;
  lastSyncAt?: string;
  eventsFailed: number;
  errorMessage?: string;
  eventsProcessed: number;
  status: 'healthy' | 'warning' | 'error' | 'inactive';
}

/**
 * Why the server chose the Debezium status (see DebeziumStatusReason in the server's monitoring types).
 */
export type DebeziumStatusReason =
  | 'check-failed'
  | 'offsets-unavailable'
  | 'slot-missing'
  | 'streaming'
  | 'delayed'
  | 'stalled'
  | 'slot-not-connected'
  | 'stopped';

/**
 * Health and status information for a service component.
 */
export interface ServiceStatus {
  service: string;
  errorRate?: number;
  lagSeconds?: number;
  lastHeartbeat?: string;
  messagesProcessed?: number;
  /** Why the status was chosen (Debezium only) */
  reason?: DebeziumStatusReason;
  /** Seconds since Debezium last saved its offsets (Debezium only) */
  lastProgressAgeSeconds?: number;
  status: 'running' | 'stopped' | 'error' | 'unknown';
}

/**
 * High-level overview of the entire CDC pipeline monitoring data.
 */
export interface MonitoringOverview {
  initialLoad: {
    activeJobs: number;
    recentJobs: JobProgress[];
  };
  tables: {
    total: number;
    active: number;
    syncing: number;
    errored: number;
  };
  dlq: {
    loaderDlq: DlqStats;
    skippedDlq: DlqStats;
    transformDlq: DlqStats;
    initialLoadDlq: DlqStats;
  };
  pipeline: {
    loaderService: ServiceStatus;
    debeziumServer: ServiceStatus;
    replicationSlot: ServiceStatus;
    transformService: ServiceStatus;
  };
}

// Telemetry types (Prometheus-backed)
/**
 * A single time-series data point for event throughput (events/min).
 */
export interface ThroughputDataPoint {
  loaded: number;
  received: number;
  recordedAt: string;
  transformed: number;
}

/**
 * A single time-series data point for latency percentiles (seconds).
 */
export interface LatencyDataPoint {
  /** p50 in seconds (null when no data) */
  p50: number | null;
  /** p95 in seconds (null when no data) */
  p95: number | null;
  /** p99 in seconds (null when no data) */
  p99: number | null;
  recordedAt: string;
}

/**
 * Latency history grouped by pipeline stage.
 */
export interface LatencyHistoryResponse {
  load: LatencyDataPoint[];
  transformation: LatencyDataPoint[];
}

/**
 * A single time-series data point for error rate.
 */
export interface ErrorRateDataPoint {
  /** Error rate as percentage (0-100) */
  errorRate: number;
  totalCount: number;
  recordedAt: string;
  failedCount: number;
}

/**
 * Current in-flight messages gauge.
 */
export interface InFlightGauge {
  value: number;
  timestamp: string;
}

/**
 * Combined telemetry response from single endpoint.
 */
export interface TelemetryResponse {
  inFlight: InFlightGauge;
  prometheusReachable: boolean;
  errorRate: ErrorRateDataPoint[];
  latency: LatencyHistoryResponse;
  throughput: ThroughputDataPoint[];
}

// Error types
/**
 * Structured error information returned by the API.
 */
export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

/**
 * Complete error response from the API including request context.
 */
export interface ApiErrorResponse {
  path?: string;
  error: ApiError;
  timestamp?: string;
  requestId?: string;
}

// WebSocket types
/**
 * Union type for all possible WebSocket event types.
 */
export type WebSocketEventType =
  | 'connection.established'
  | 'connection.error'
  | 'dlq.message.added'
  | 'dlq.message.retried'
  | 'dlq.message.purged'
  | 'dlq.stats.updated'
  | 'pipeline.log.batch'
  | 'pipeline.log.status'
  | 'pipeline.status.changed'
  | 'job.progress.updated'
  | 'job.status.changed'
  | 'mapping.updated'
  | 'mapping.activated'
  | 'mapping.deactivated'
  | 'subscription.updated'
  | 'ping'
  | 'pong';

/**
 * Generic WebSocket event message with type and payload.
 */
export interface WebSocketEvent<T = unknown> {
  data: T;
  timestamp: string;
  type: WebSocketEventType;
}

/**
 * WebSocket event payload for DLQ message actions.
 */
export interface DlqMessageEvent {
  messageId: string;
  action: 'added' | 'retried' | 'purged';
  dlqType: 'transform' | 'loader' | 'skipped' | 'initial_load';
}

/**
 * WebSocket event payload for DLQ statistics updates.
 */
export interface DlqStatsEvent {
  messageCount: number;
  oldestMessageAge?: number;
  dlqType: 'transform' | 'loader' | 'skipped' | 'initial_load';
}

/**
 * WebSocket event payload for pipeline service status changes. It carries no status;
 * fetch the monitoring overview again to read the new status.
 */
export interface PipelineStatusEvent {
  errorRate?: number;
  environment: string;
  retailerCode: string;
  messagesProcessed?: number;
  service: 'transform' | 'loader' | 'debezium' | 'replication-slot';
}

/**
 * WebSocket event payload for job progress updates.
 */
export interface JobProgressEvent {
  jobId: string;
  totalRows: number;
  completedAt?: string;
  processedRows: number;
  status: JobProgress['status'];
}

/**
 * WebSocket event payload for mapping configuration changes.
 */
export interface MappingChangeEvent {
  version: number;
  mappingKey: string;
  action: 'updated' | 'activated' | 'deactivated';
}

/**
 * WebSocket event payload for connection establishment with subscriptions.
 */
export interface ConnectionEstablishedEvent {
  userId: string;
  clientId: string;
  subscriptions: string[];
}

/**
 * WebSocket event payload for a batch of pipeline log lines.
 */
export interface PipelineLogBatchEvent {
  /** Array of formatted log lines with ANSI color codes */
  lines: string[];
}

/**
 * WebSocket event payload for pipeline log relay connection status.
 */
export interface PipelineLogStatusEvent {
  /** Current log relay connection status */
  status: 'connected' | 'disconnected' | 'reconnecting' | 'offline';
}

/**
 * Union type for subscribable WebSocket topics.
 */
export type WebSocketTopic = 'dlq' | 'logs' | 'pipeline' | 'jobs' | 'mappings' | 'all';

/**
 * Union type for WebSocket connection states.
 */
export type WebSocketConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

// =============================================================================
// TABLE COLUMN DISCOVERY TYPES
// =============================================================================

/**
 * Represents a single column from a source database table.
 */
export interface ColumnInfo {
  /** Column name. */
  name: string;
  /** PostgreSQL data type (e.g., varchar, integer). */
  dataType: string;
  /** Whether column allows NULL. */
  isNullable: boolean;
  /** Whether column is part of the primary key. */
  isPrimaryKey: boolean;
  /** Column order in the table. */
  ordinalPosition: number;
}

/**
 * Data payload for the source table box node on the canvas.
 */
export interface SourceTableNodeData {
  /** Discriminator for node type. */
  isSource: true;
  /** Source table name. */
  tableName: string;
  /** Columns fetched from backend. */
  columns: ColumnInfo[];
  /** Source fields that no longer exist in the current schema. */
  staleFields?: string[];
  /** Whether column loading failed with an error. */
  columnsError?: boolean;
  /** Whether columns are still being loaded from the backend. */
  columnsLoading?: boolean;
  /** Whether the table name field has a validation error. */
  tableNameError?: boolean;
  /** Callback to remove this source table from the canvas (only for non-primary tables). */
  onRemove?: (tableName: string) => void;
  /** Callback to open the Add Join dialog from this source node's header. Passes the table name. */
  onAddJoin?: (tableName: string) => void;
}

/**
 * Rule types that require no source field (generated columns only).
 * Used to filter edge dropdowns and migrate legacy data.
 */
export const SOURCELESS_RULE_TYPES = new Set<string>([RuleType.SYNTHETIC, RuleType.METADATA]);

/**
 * A user-defined target column.
 */
export interface TargetColumn {
  /** Column name (user-entered). */
  name: string;
  /** Rule type for generated columns. */
  ruleType?: RuleType;
  /** True for synthetic/metadata columns (no source edge). */
  isGenerated: boolean;
  /** Rule-specific config for generated columns. */
  config?: Record<string, unknown>;
}

/**
 * Data payload for the target table box node on the canvas.
 */
export interface TargetTableNodeData {
  /** Discriminator for node type. */
  isSource: false;
  /** Target table name. */
  tableName: string;
  /** User-defined target columns. */
  columns: TargetColumn[];
  /** Whether the table name field has a validation error. */
  tableNameError?: boolean;
  /** Maps target column name → source column data type (derived from connected edges) */
  sourceTypeMap?: Record<string, string>;
}

/**
 * Custom data payload stored on each edge connecting a source column to a target column.
 */
export interface MappingEdgeData {
  /** One of the 8 transformation rule types */
  ruleType: RuleType;
  /** Source column name. */
  sourceField: string;
  /** Target column name. */
  targetField: string;
  /** If true, edge is visual-only and excluded from serialization (many-to-one de-duplication). */
  isVisualOnly?: boolean;
  /** Rule-specific configuration. */
  config: Record<string, unknown>;
}

// =============================================================================
// PUBLICATION SYNC TYPES
// =============================================================================

/**
 * Publication sync status.
 *
 * - `synced`  — Publication matches LDA config exactly.
 * - `partial` — Publication exists but has missing or extra tables.
 * - `failed`  — Sync was attempted and failed.
 * - `pending` — Sync has not yet completed (transient state after save).
 * - `error`   — A permanent error prevents sync (e.g., missing credentials, invalid environment).
 * - `unknown` — Sync status cannot be determined (e.g., source DB unreachable in read-only check).
 */
export type SyncStatus = 'synced' | 'partial' | 'failed' | 'pending' | 'error' | 'unknown';

/** Sync error for a specific table. */
export interface SyncError {
  table: string;
  message: string;
  operation: 'add' | 'drop' | 'grant' | 'replica_identity';
}

/** Response from GET sync-status endpoint. */
export interface SyncStatusResponse {
  /** Human-readable message explaining the status (set when syncStatus is 'unknown' or 'error'). */
  message?: string;
  ldaTables: string[];
  syncStatus: SyncStatus;
  publicationTables: string[];
  extraInPublication: string[];
  missingFromPublication: string[];
  healthCheck: {
    message?: string;
    canManagePublication: boolean;
  };
}

/** Response from POST sync (retry) endpoint. */
export interface RetrySyncResponse {
  added: string[];
  granted: string[];
  removed: string[];
  status: SyncStatus;
  errors: SyncError[];
}
